import { useState, useEffect } from 'react';

// material-ui
import {
    Typography,
    Box,
    Container,
    styled,
    Paper,
    InputLabel,
    TextField,
    Grid,
    FormControl,
    Autocomplete,
    CircularProgress
} from '@mui/material';

// project imports
import HomeLayout from 'layout/HomeLayout';
import { BannerType } from 'types';
import ImdaButton from 'components/button';
import SearchIcon from '@mui/icons-material/Search';
import SidebarDatabaseLink from 'features/databasePage/common/SidebarDatabaseLink';
import RegisteredContentAssesorsTable from './RegisteredContentAssesorsTable';
import { useSelector } from 'store';
import { getRCAListForLanguageProficiency } from 'store/api/databases.api';
import { DataType } from './type';

const BANNER_PROPS: BannerType = {
    title: 'List of Registered Content Assesors',
    subTitle: '',
    type: 'purple'
};

const SectionTitle = styled(Typography)({
    fontSize: '18px',
    fontWeight: 700,
    color: '#01131B'
});

const StyledImdaButton = styled(ImdaButton)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        width: '150px'
    },
    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

// ==============================|| REGISTERED CONTENT ASSESSORS PAGE ||============================== //

function RegisteredContentAssesorsDatabase() {
    const { dropdownOpts } = useSelector((state) => state.appForm);
    const [selectedLanguageGroup, setSelectedLanguageGroup] = useState<any>(null);
    const [otherLanguage, setOtherLanguage] = useState<string>('');
    const [showOthersField, setShowOthersField] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<DataType[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const pageSize = 10;

    useEffect(() => {
        // Check if "Others" (NA) is selected
        if (selectedLanguageGroup?.optID === 'NA') {
            setShowOthersField(true);
        } else {
            setShowOthersField(false);
            setOtherLanguage('');
        }
    }, [selectedLanguageGroup]);

    const handleSearch = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        if (!selectedLanguageGroup) {
            return;
        }

        // Check if Others (NA) is selected
        const isOthers = selectedLanguageGroup.optID === 'NA';
        
        if (isOthers && !otherLanguage.trim()) {
            return;
        }

        // When Others is selected, use the optID and pass otherLanguage as separate parameter
        const languageCode = selectedLanguageGroup.optID;
        const otherLangParam = isOthers ? otherLanguage.trim() : undefined;

        setIsLoading(true);
        setHasSearched(true);
        setCurrentPage(1);
        setErrorMessage('');

        try {
            const response = await getRCAListForLanguageProficiency(languageCode, 1, pageSize, otherLangParam);
            
            if (response && response.pxResults) {
                const formattedData: DataType[] = response.pxResults.map((item: any) => ({
                    name: item.StringVal2 || '',
                    email: item.StringVal3 || '',
                    language_proficiency: item.StringVal1 || ''
                }));
                
                setSearchResults(formattedData);
                // Use pxTotalResultCount for total records across all pages
                const totalRecords = parseInt(response.pxTotalResultCount || response.pxResultCount) || 0;
                setTotalCount(totalRecords);
            } else {
                setSearchResults([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching RCA list:', error);
            setErrorMessage('No results found. Please try searching with a different language group.');
            setSearchResults([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = async (page: string) => {
        const pageNumber = parseInt(page);
        setCurrentPage(pageNumber);

        if (!selectedLanguageGroup) return;

        // Scroll to top of results
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Check if Others (NA) is selected
        const isOthers = selectedLanguageGroup.optID === 'NA';
        const languageCode = selectedLanguageGroup.optID;
        const otherLangParam = isOthers ? otherLanguage.trim() : undefined;

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await getRCAListForLanguageProficiency(languageCode, pageNumber, pageSize, otherLangParam);
            
            if (response && response.pxResults) {
                const formattedData: DataType[] = response.pxResults.map((item: any) => ({
                    name: item.StringVal2 || '',
                    email: item.StringVal3 || '',
                    language_proficiency: item.StringVal1 || ''
                }));
                
                setSearchResults(formattedData);
                // Use pxTotalResultCount for total records across all pages
                const totalRecords = parseInt(response.pxTotalResultCount || response.pxResultCount) || 0;
                setTotalCount(totalRecords);
            }
        } catch (error) {
            console.error('Error fetching RCA list:', error);
            setErrorMessage('Unable to load the requested page. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setSelectedLanguageGroup(null);
        setOtherLanguage('');
        setShowOthersField(false);
        setSearchResults([]);
        setTotalCount(0);
        setCurrentPage(1);
        setHasSearched(false);
        setErrorMessage('');
    };

    return (
        <HomeLayout bannerProps={BANNER_PROPS} sideBar={<SidebarDatabaseLink />}>
            <Container sx={{ p: { xs: '16px', sm: '24px 16px', md: '48px 16px' } }}>
                <Paper component="form" sx={{ p: '16px' }} onSubmit={handleSearch}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <InputLabel 
                                variant="standard" 
                                required
                                sx={{
                                    '& .MuiFormLabel-asterisk': {
                                        color: '#C20000'
                                    }
                                }}
                            >
                                Language Group
                            </InputLabel>
                            <FormControl fullWidth>
                                <Autocomplete
                                    options={dropdownOpts?.LanguageProficiency || []}
                                    value={selectedLanguageGroup}
                                    onChange={(_, newValue) => setSelectedLanguageGroup(newValue)}
                                    getOptionLabel={(option) => option.optLabel as string}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: 10002
                                            }
                                        }
                                    }}
                                    sx={{
                                        '& input': {
                                            padding: '0!important',
                                            width: '100%!important'
                                        },
                                        '& .MuiAutocomplete-inputRoot': {
                                            padding: '10px 8px!important',
                                            width: '100%'
                                        },
                                        '& .Mui-disabled': { background: '#EDEDED', overflow: 'hidden' }
                                    }}
                                    renderInput={(params) => <TextField {...params} placeholder="Select Language Group" required />}
                                />
                            </FormControl>
                        </Grid>

                        {showOthersField && (
                            <Grid item xs={12} md={6}>
                                <InputLabel 
                                    variant="standard" 
                                    required
                                    sx={{
                                        '& .MuiFormLabel-asterisk': {
                                            color: '#C20000'
                                        }
                                    }}
                                >
                                    Other Language
                                </InputLabel>
                                <TextField
                                    fullWidth
                                    required
                                    placeholder="Enter a language name"
                                    value={otherLanguage}
                                    onChange={(e) => setOtherLanguage(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            padding: '10px 8px!important'
                                        },
                                        '& input': {
                                            padding: '0!important'
                                        }
                                    }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: { md: 'flex-end', xs: 'center' }, alignItems: 'center', gap: 2 }}>
                                {hasSearched && (
                                    <StyledImdaButton 
                                        topSpace 
                                        onClick={handleClear}
                                        btnClass="secondary"
                                        disabled={isLoading}
                                    >
                                        Clear
                                    </StyledImdaButton>
                                )}
                                <StyledImdaButton 
                                    topSpace 
                                    type="submit"
                                    endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                    disabled={isLoading || !selectedLanguageGroup || (showOthersField && !otherLanguage.trim())}
                                >
                                    {isLoading ? 'Searching...' : 'Search'}
                                </StyledImdaButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>

            {hasSearched && (
                <Container>
                    <SectionTitle>Content Assesors List</SectionTitle>
                    {errorMessage ? (
                        <Typography variant="body1" sx={{ mb: '16px', color: 'error.main' }}>
                            {errorMessage}
                        </Typography>
                    ) : (
                        <Typography variant="body1" sx={{ mb: '16px' }}>
                            Total Number of Records: {totalCount}
                        </Typography>
                    )}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <CircularProgress />
                        </Box>
                    ) : errorMessage ? null : (
                        <RegisteredContentAssesorsTable 
                            data={searchResults} 
                            count={totalCount} 
                            page={currentPage} 
                            onSetPage={handlePageChange}
                        />
                    )}
                </Container>
            )}
        </HomeLayout>
    );
}

export default RegisteredContentAssesorsDatabase;
